import { create } from 'zustand'

/**
 * The main room, as a broadcast target.
 *
 * Everyone who has not walked into a breakout is standing in the main room, so
 * it has to be addressable — otherwise a targeted message cannot reach them at
 * all. Reserved rather than derived from the meeting id so a breakout can never
 * be created with the same id and silently become "everyone who stayed put".
 * Must match `@main_room_target` in room_channel.ex.
 */
export const MAIN_ROOM_TARGET_ID = '__main__'
export const MAIN_ROOM_NAME = 'Main room'

/**
 * Broadcast messages a host sends into breakout rooms.
 *
 * The thread is the same for everyone; what differs is who is reading it. Both
 * the host's composer and the participant's viewer render from here, so the two
 * cannot show the same conversation differently.
 */
export type BreakoutBroadcastMessage = {
  id: string
  text: string
  /** Display name of whoever sent it. */
  senderName: string
  /**
   * WHO sent it. Several people can hold host or co-host, so "is this mine"
   * cannot be answered by display name — two hosts may share one, and a host
   * renaming themselves would change the answer for messages already sent.
   */
  senderId: string | null
  /** Epoch ms. Rendered as a time, stored as a number so ordering is not a
   *  string comparison. */
  sentAt: number
  /** Rooms this went to. Empty means every room. */
  roomIds: string[]
  /** Room names, resolved by the sender or by the server. Empty alongside a
   *  non-empty `roomIds` means the names could not be resolved — the UI says
   *  how many rooms rather than inventing names. */
  roomNames: string[]
}

type State = {
  /**
   * This client's peer id, published here by the sync hook rather than threaded
   * down as a prop. The broadcast screens sit deep inside the control bar,
   * which does not know who the local peer is and has no other reason to.
   */
  myPeerId: string | null
  messages: BreakoutBroadcastMessage[]
  /** Rooms the composer is currently addressing. Empty = all rooms. */
  selectedRoomIds: string[]
  /** How many of `messages` have been seen. The badge counts the rest. */
  readCount: number
  send: (m: Omit<BreakoutBroadcastMessage, 'id' | 'sentAt'>) => string
  receive: (m: BreakoutBroadcastMessage) => void
  setMyPeerId: (id: string | null) => void
  markRead: () => void
  unreadCount: () => number
  setSelectedRoomIds: (ids: string[]) => void
  toggleRoom: (id: string) => void
  selectAllRooms: () => void
  clear: () => void
}

let seq = 0

export const useBreakoutBroadcastStore = create<State>((set, get) => ({
  myPeerId: null,
  messages: [],
  selectedRoomIds: [],
  readCount: 0,

  setMyPeerId: (id) => set((s) => (s.myPeerId === id ? s : { myPeerId: id })),

  /** Appends locally and returns the id, which the caller sends to the server
   *  so the echo can be recognised rather than shown twice. Sending is reading:
   *  your own message is not something to notify you about. */
  send: (m) => {
    const id = `bb-${Date.now()}-${++seq}`
    set((s) => {
      const messages = [...s.messages, { ...m, id, sentAt: Date.now() }]
      return { messages, readCount: messages.length }
    })
    return id
  },

  /** For the transport. Ignores a duplicate id so a message echoed back to its
   *  own sender does not appear twice. */
  receive: (m) =>
    set((s) => (s.messages.some((x) => x.id === m.id) ? s : { messages: [...s.messages, m] })),

  markRead: () => set((s) => ({ readCount: s.messages.length })),

  unreadCount: () => Math.max(0, get().messages.length - get().readCount),

  setSelectedRoomIds: (ids) => set({ selectedRoomIds: ids }),

  toggleRoom: (id) =>
    set((s) => ({
      selectedRoomIds: s.selectedRoomIds.includes(id)
        ? s.selectedRoomIds.filter((r) => r !== id)
        : [...s.selectedRoomIds, id],
    })),

  /** "All rooms" is the empty selection, not every id listed. A room created
   *  after the host chose "all" should still receive — enumerating ids would
   *  quietly exclude it. */
  selectAllRooms: () => set({ selectedRoomIds: [] }),

  clear: () => set({ messages: [], selectedRoomIds: [], readCount: 0 }),
}))
